import { Car, Navigation, Plane } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Eyebrow, FadeIn, GoldButton } from "@/components/Reveal";
import { GETTING_HERE } from "@/data/island";

export default function GettingHerePage() {
  return (
    <>
      <PageHero eyebrow="Find Us" title={GETTING_HERE.title} image={GETTING_HERE.heroImage}>
        {GETTING_HERE.intro}
      </PageHero>

      <section className="bg-navy-800 py-24 lg:py-28">
        <div className="mx-auto grid max-w-7xl gap-16 px-5 sm:px-8 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <Eyebrow>By Car</Eyebrow>
            <FadeIn delay={0.1}>
              <ul className="mt-8 border-t border-white/10" data-testid="driving-routes">
                {GETTING_HERE.routes.map((r) => (
                  <li
                    key={r.from}
                    className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 py-5"
                  >
                    <span className="flex items-center gap-4">
                      <Car className="h-4 w-4 text-gold" />
                      <span className="text-sm text-ivory sm:text-base">{r.from}</span>
                    </span>
                    <span className="text-right">
                      <span className="block text-[12px] text-mist">{r.via}</span>
                      <span className="block text-[12px] font-medium text-gold">{r.time}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </FadeIn>

            <FadeIn delay={0.15}>
              <div className="mt-12 grid gap-6 sm:grid-cols-2">
                <div className="border border-white/10 bg-navy-900/50 p-6">
                  <span className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.24em] text-gold">
                    <Navigation className="h-4 w-4" /> By Public Transit
                  </span>
                  <p className="mt-4 text-sm font-light leading-relaxed text-mist">
                    Regular buses and trains serve Westport from Dublin and Galway. From Westport
                    town, Rosmoney Pier is a short taxi ride &mdash; then the RIB brings you to the
                    island.
                  </p>
                </div>
                <div className="border border-white/10 bg-navy-900/50 p-6">
                  <span className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.24em] text-gold">
                    <Plane className="h-4 w-4" /> By Air
                  </span>
                  <p className="mt-4 text-sm font-light leading-relaxed text-mist">
                    Knock Airport (Ireland West) is approximately 1 hour from Westport, with Dublin
                    Airport around 3 hours by road.
                  </p>
                </div>
              </div>
            </FadeIn>
          </div>

          <div className="lg:col-span-2">
            <FadeIn delay={0.1}>
              <div className="border border-gold/30 bg-navy-900/70 p-8 lg:sticky lg:top-28">
                <h2 className="font-serif text-3xl text-ivory">The Centre</h2>
                <p className="mt-6 text-sm leading-relaxed text-ivory/90">
                  The Adventure Islands Centre
                  <br />
                  Collanmore Island, Clew Bay,
                  <br />
                  Westport, Co Mayo, Ireland
                </p>
                <div className="mt-8 border-l-2 border-gold/60 bg-navy-800/60 p-5">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-gold">
                    The Crossing
                  </p>
                  <p className="mt-3 text-sm font-light leading-relaxed text-mist">
                    {GETTING_HERE.crossing}
                  </p>
                </div>
                <GoldButton href="/contact" testId="getting-here-cta" className="mt-8 w-full">
                  Enquire Now
                </GoldButton>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="bg-navy-900 py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn>
            <div className="relative overflow-hidden border border-white/10">
              <img
                src="/images/trip-to-island.webp"
                alt="The high-speed RIB trip across Clew Bay to Collanmore Island"
                data-testid="crossing-image"
                className="h-[440px] w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-950/80 via-transparent to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-8">
                <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-gold">The Crossing</p>
                <h3 className="mt-2 max-w-xl font-serif text-3xl text-ivory sm:text-4xl">
                  Two minutes by high-speed RIB &mdash; the trip to the island is part of the adventure
                </h3>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </>
  );
}
